import { supportsWebp } from "helpers/webpSupport";
import { remoteConfig } from "services/remoteConfig";

/**
 * WebP for CMS/Azure-hosted images (the bundled-asset counterpart lives in
 * webpSupport.js). The admin portal saves a `.webp` sibling next to every
 * uploaded PNG/JPEG at the same blob path, so a supporting browser can load
 * `foo.webp` instead of `foo.png` by convention — no API change involved.
 *
 * cmsImageUrl(url) is the one entry point: it swaps the extension only when
 *   - the browser decodes webp (supportsWebp — the ?nowebp QA override works
 *     here too),
 *   - the CmsWebpEnabled remote-config flag is on (kept off until the blob
 *     backfill has run, because a missing sibling 404s and CSS backgrounds
 *     have no onError to fall back with),
 *   - the URL is a gamecity blob PNG/JPEG (data: URLs, bundled assets, mp4s,
 *     svg/gif and foreign hosts all pass through untouched),
 *   - the sibling hasn't already failed to load this session.
 *
 * A capture-phase error listener rescues every <img> that 404s on a swapped
 * URL — it restores the original src and blacklists that sibling so later
 * renders (including CSS background sites) go straight to the original.
 */

const BLOB_PREFIX = "https://gamecity.blob.core.windows.net/";
const SWAPPABLE_EXT = /\.(png|jpe?g)$/i;

// webp URL -> original URL, for the error-rescue listener.
const swapped = new Map();
// Siblings that 404'd this session; never retried.
const failed = new Set();

/**
 * The `.webp` sibling URL, or null when this URL shouldn't be swapped.
 * Query strings are left alone deliberately — admin blob URLs don't carry
 * them, and a SAS-signed URL must never be rewritten.
 */
export const siblingUrl = (url) => {
    if (typeof url !== "string" || !url.startsWith(BLOB_PREFIX)) return null;
    if (url.includes("?")) return null;
    if (!SWAPPABLE_EXT.test(url)) return null;
    return url.replace(SWAPPABLE_EXT, ".webp");
};

/** URL to actually render: the webp sibling when eligible, else the input. */
export const cmsImageUrl = (url) => {
    if (!supportsWebp || !remoteConfig.isCmsWebpEnabled()) return url;
    const webp = siblingUrl(url);
    if (!webp || failed.has(webp)) return url;
    swapped.set(webp, url);
    return webp;
};

// Global <img> rescue. Image load errors don't bubble, so listen in the
// capture phase; anything that fails on a URL we swapped gets its original
// back and the sibling is blacklisted for the session.
if (typeof document !== "undefined") {
    document.addEventListener(
        "error",
        (e) => {
            const el = e.target;
            if (!el || el.tagName !== "IMG") return;
            const original = swapped.get(el.src);
            if (!original) return;
            failed.add(el.src);
            el.src = original;
        },
        true
    );
}
